import React from 'react';
import { Table, Badge, Button } from '@/components/ui';
import type { MaintenanceWorkload, WorkloadStatus } from '../types/maintenance-workload.types';

export interface WorkloadDesktopTableProps {
  data: MaintenanceWorkload[];
  isLoading: boolean;
  onEdit: (workload: MaintenanceWorkload) => void;
  onDelete: (workload: MaintenanceWorkload) => void;
  onRowClick: (workload: MaintenanceWorkload) => void;
}

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  calculated: 'bg-blue-100 text-blue-600',
  reviewing: 'bg-amber-100 text-amber-600',
  approved: 'bg-emerald-100 text-emerald-600',
  planned: 'bg-os-primary/10 text-os-primary',
  archived: 'bg-slate-200 text-slate-500',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản nháp',
  calculated: 'Đã tính toán',
  reviewing: 'Đang soát xét',
  approved: 'Đã duyệt',
  planned: 'Đã lập kế hoạch',
  archived: 'Lưu trữ',
};

export function WorkloadDesktopTable({ data, isLoading, onEdit, onDelete, onRowClick }: WorkloadDesktopTableProps) {
  return (
    <Table
      columns={[
        { key: 'title', title: 'Workload' },
        { key: 'project', title: 'Dự án / Khu vực' },
        { key: 'period', title: 'Chu kỳ' },
        { key: 'man_hours', title: 'Giờ công', align: 'right' },
        { key: 'material', title: 'Vật tư', align: 'right' },
        { key: 'score', title: 'Điểm tải', align: 'center' },
        { key: 'status', title: 'Trạng thái', align: 'center' },
        { key: 'actions', title: '', align: 'right' },
      ]}
      data={data}
      isLoading={isLoading}
      onRowClick={onRowClick}
      renderCell={(item, key) => {
        switch (key) {
          case 'title':
            return (
              <div className="flex flex-col">
                <span className="font-bold text-slate-900">{item.title}</span>
                <span className="text-[10px] text-slate-400 font-mono ">ID: {item.id.split('-')[0]}</span>
              </div>
            );
          case 'project':
            return (
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-slate-700">{item.project?.name}</span>
                <span className="text-xs text-slate-500">{item.zone?.zone_name}</span>
              </div>
            );
          case 'period':
            return (
              <div className="flex items-center gap-1 text-xs text-slate-600">
                <i className="ri-calendar-line" />
                <span>{new Date(item.period_start).toLocaleDateString('vi-VN')} - {new Date(item.period_end).toLocaleDateString('vi-VN')}</span>
              </div>
            );
          case 'man_hours':
            return <span className="font-bold text-slate-900">{item.estimated_man_hours?.toLocaleString()}h</span>;
          case 'material':
            return <span className="font-bold text-slate-900">{item.estimated_material_cost?.toLocaleString()}đ</span>;
          case 'score':
            return (
              <div className="flex flex-col items-center gap-1">
                <span className={`font-bold ${item.total_workload_score > 80 ? 'text-red-500' : 'text-os-primary'}`}>
                  {item.total_workload_score}
                </span>
                <div className="w-12 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${item.total_workload_score > 80 ? 'bg-red-500' : 'bg-os-primary'}`}
                    style={{ width: `${item.total_workload_score}%` }}
                  />
                </div>
              </div>
            );
          case 'status':
            return (
              <Badge className={`${statusColors[item.status]} border-transparent text-[10px] font-bold `}>
                {statusLabels[item.status]}
              </Badge>
            );
          case 'actions':
            return (
              <div className="flex justify-end gap-1">
                <Button variant="ghost" size="sm" icon="ri-edit-line" onClick={(e) => { e.stopPropagation(); onEdit(item); }} />
                <Button variant="ghost" size="sm" icon="ri-delete-bin-line" className="text-red-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); onDelete(item); }} />
              </div>
            );
          default:
            return null;
        }
      }}
    />
  );
}
